<template>
  <div class="card">
    <div class="card-header d-flex align-items-center justify-content-between flex-wrap">
      <div>
        <h4 class="card-title mb-0">
          Заявки на изменение доп. полей
        </h4>
        <small class="text-muted">
          Подтверждение изменений оплаченных дополнительных опций
        </small>
      </div>
      <div class="d-flex align-items-center mt-50">
        <b-form-select
          v-model="statusFilter"
          :options="statusOptions"
          size="sm"
          class="mr-1 status-filter"
          @change="loadRequests"
        />
        <b-button
          variant="outline-primary"
          size="sm"
          :disabled="!startId || loading"
          @click="loadRequests"
        >
          <b-spinner
            v-if="loading"
            small
            class="mr-50"
          />
          Обновить
        </b-button>
      </div>
    </div>
    <div class="card-body">
      <b-alert
        v-if="loadError"
        show
        variant="danger"
        class="mb-2"
      >
        {{ loadError }}
      </b-alert>

      <b-table
        :items="requests"
        :fields="tableFields"
        striped
        responsive
        small
        :busy="loading"
        empty-text="Заявок нет"
        show-empty
      >
        <template #cell(participant)="data">
          <div class="small">
            <div>
              {{ participantName(data.item) }}
            </div>
            <div class="text-muted">
              {{ participantEmail(data.item) }}
            </div>
          </div>
        </template>

        <template #cell(field)="data">
          {{ fieldName(data.item) }}
        </template>

        <template #cell(values)="data">
          <div class="small">
            <div>
              <span class="text-muted">Было:</span>
              {{ data.item.display_old_value || '—' }}
            </div>
            <div>
              <span class="text-muted">Станет:</span>
              {{ data.item.display_new_value || '—' }}
            </div>
            <div
              v-if="data.item.surcharge_amount > 0"
              class="mt-50"
            >
              <span class="text-muted">Доплата:</span>
              {{ formatSurcharge(data.item.surcharge_amount) }} ₽
            </div>
          </div>
        </template>

        <template #cell(status)="data">
          <b-badge :variant="statusVariant(data.item.status)">
            {{ statusLabel(data.item.status) }}
          </b-badge>
          <div
            v-if="data.item.status === 'rejected' && data.item.rejection_reason"
            class="small text-danger mt-50"
          >
            {{ data.item.rejection_reason }}
          </div>
        </template>

        <template #cell(createdAt)="data">
          {{ formatDate(data.item.createdAt) }}
        </template>

        <template #cell(actions)="data">
          <div
            v-if="data.item.status === 'pending'"
            class="d-flex flex-wrap"
          >
            <b-button
              size="sm"
              variant="success"
              class="mr-50 mb-50"
              :disabled="actionId === data.item.id"
              @click="approve(data.item.id)"
            >
              Подтвердить
            </b-button>
            <b-button
              size="sm"
              variant="outline-danger"
              class="mb-50"
              :disabled="actionId === data.item.id"
              @click="openRejectModal(data.item)"
            >
              Отклонить
            </b-button>
          </div>
          <span
            v-else
            class="text-muted"
          >—</span>
        </template>

        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner
              small
              class="align-middle mr-1"
            />
            <strong>Загрузка...</strong>
          </div>
        </template>
      </b-table>
    </div>

    <b-modal
      id="afcr-reject-modal"
      v-model="rejectModalVisible"
      title="Отклонить изменение"
      ok-title="Отклонить"
      ok-variant="danger"
      cancel-title="Отмена"
      :ok-disabled="!rejectReason.trim() || rejectLoading"
      @ok="confirmReject"
    >
      <p class="mb-1">
        Укажите причину отказа. Она будет показана участнику в личном кабинете.
      </p>
      <b-form-textarea
        v-model="rejectReason"
        rows="3"
        maxlength="1000"
        placeholder="Причина отказа"
      />
    </b-modal>
  </div>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BFormSelect,
  BFormTextarea,
  BModal,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from '@axios'
import config from '@core/app-config/api-config'

export default {
  name: 'AdditionalFieldChangeRequestsQueue',
  components: {
    BAlert,
    BBadge,
    BButton,
    BFormSelect,
    BFormTextarea,
    BModal,
    BSpinner,
    BTable,
  },
  props: {
    startId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      loadError: '',
      requests: [],
      statusFilter: 'pending',
      statusOptions: [
        { value: 'pending', text: 'Ожидают' },
        { value: 'awaiting_payment', text: 'Ожидают доплаты' },
        { value: 'approved', text: 'Подтверждённые' },
        { value: 'rejected', text: 'Отклонённые' },
        { value: '', text: 'Все' },
      ],
      tableFields: [
        { key: 'participant', label: 'Участник' },
        { key: 'field', label: 'Поле' },
        { key: 'values', label: 'Изменение' },
        { key: 'status', label: 'Статус' },
        { key: 'createdAt', label: 'Создано' },
        { key: 'actions', label: 'Действия' },
      ],
      actionId: null,
      rejectModalVisible: false,
      rejectReason: '',
      rejectLoading: false,
      rejectTargetId: null,
    }
  },
  watch: {
    startId: {
      immediate: true,
      handler(value) {
        if (value) {
          this.loadRequests()
        }
      },
    },
  },
  methods: {
    participantName(item) {
      const member = item && item.memberStart
      if (!member) {
        return '—'
      }

      return [member.surname, member.name].filter(Boolean).join(' ') || '—'
    },
    participantEmail(item) {
      const member = item && item.memberStart
      return (member && member.email) || '—'
    },
    fieldName(item) {
      const field = item && item.additionalField
      return (field && field.name) || '—'
    },
    toast(title, text, variant = 'success') {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          text,
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          variant,
        },
      })
    },
    statusLabel(status) {
      if (status === 'pending') return 'Ожидает'
      if (status === 'awaiting_payment') return 'Ожидает доплаты'
      if (status === 'approved') return 'Подтверждено'
      if (status === 'rejected') return 'Отклонено'
      return status
    },
    statusVariant(status) {
      if (status === 'pending') return 'warning'
      if (status === 'awaiting_payment') return 'info'
      if (status === 'approved') return 'success'
      if (status === 'rejected') return 'danger'
      return 'secondary'
    },
    formatSurcharge(kopecks) {
      return (Number(kopecks) || 0) / 100
    },
    formatDate(value) {
      if (!value) return '—'
      return this.$moment(value).format('DD.MM.YYYY HH:mm')
    },
    async loadRequests() {
      if (!this.startId) {
        return
      }

      this.loading = true
      this.loadError = ''

      try {
        const params = {}
        if (this.statusFilter) {
          params.status = this.statusFilter
        }

        const response = await axios.get(
          `${config.host}/member-start/start/${this.startId}/additional-field-change-requests`,
          { params },
        )

        this.requests = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        this.loadError = error.response?.data?.message || 'Не удалось загрузить заявки'
        this.requests = []
      } finally {
        this.loading = false
      }
    },
    async approve(requestId) {
      this.actionId = requestId

      try {
        const response = await axios.post(
          `${config.host}/member-start/additional-field-change-requests/${requestId}/approve`,
        )
        const payload = response.data || {}
        if (payload.status === 'awaiting_payment') {
          const amount = (Number(payload.surcharge_amount) || 0) / 100
          this.toast(
            'Одобрено',
            `Изменение одобрено. Участнику нужно доплатить ${amount} ₽ в личном кабинете.`,
          )
        } else {
          this.toast('Готово', 'Изменение подтверждено и применено')
        }
        await this.loadRequests()
        this.$emit('updated')
      } catch (error) {
        this.toast('Ошибка', error.response?.data?.message || 'Не удалось подтвердить', 'danger')
      } finally {
        this.actionId = null
      }
    },
    openRejectModal(item) {
      this.rejectTargetId = item.id
      this.rejectReason = ''
      this.rejectModalVisible = true
    },
    async confirmReject(event) {
      event.preventDefault()

      if (!this.rejectReason.trim()) {
        return
      }

      this.rejectLoading = true
      this.actionId = this.rejectTargetId

      try {
        await axios.post(
          `${config.host}/member-start/additional-field-change-requests/${this.rejectTargetId}/reject`,
          { reason: this.rejectReason.trim() },
        )
        this.rejectModalVisible = false
        this.toast('Готово', 'Изменение отклонено')
        await this.loadRequests()
        this.$emit('updated')
      } catch (error) {
        this.toast('Ошибка', error.response?.data?.message || 'Не удалось отклонить', 'danger')
      } finally {
        this.rejectLoading = false
        this.actionId = null
      }
    },
  },
}
</script>

<style scoped>
.status-filter {
  min-width: 160px;
}
</style>
